import { useState } from 'react';

import { Box, Card, H1, H2, ListRow, Separator } from '@betterment/samba-react';
import {
  PayrollConfigsList,
  RecurringTransactionsConfig,
  RecurringTransactionsDisclosure,
  RecurringTransactionsSetup,
  SambaAutoLink,
} from '@betterment/shared-components';

import { trackClick } from '../../../features/track';
import { handleSetupRecurringDeposit } from '../../../forecaster/purpose/components/settings-card/utils';
import { PayrollContributionConfigFragment } from '../../../graphql/fragments/payrollContributionConfig.generated';
import { RecurringTransactionConfigFragment } from '../../../graphql/fragments/recurringTransactionConfig.generated';
import { LegacyTransferType } from '../../../graphql/schema';
import { PurposeRecurringTransactionConfigsQuery } from '../../../purpose/components/recurring-transactions-card/graphql/purposeRecurringTransactionConfigs.generated';
import { useMessageVisibility } from '../../hooks/useMessageVisibility';
import { urlBuilder } from '../../urlBuilder';
import MessageBox from '../message-box/message-box';

type Envelope = PurposeRecurringTransactionConfigsQuery['purpose']['envelope'];
type LegalSubAccount = Extract<Envelope['legalSubAccounts'][number], { __typename: 'CashAccount' }>;
type Disclosure = LegalSubAccount['recurringDepositMotivation'];

type RecurringTransactionsCardViewProps = {
  recurringTransactionConfigs: RecurringTransactionConfigFragment[];
  isWithdrawal: (sourceLegacySubAccountId: number) => boolean;
  payrollContributionConfigs?: PayrollContributionConfigFragment[];
  recurringTransactionGuidance?: Envelope['recurringTransactionMessages'];
  disclosures?: Disclosure;
  subAccountId: string;
};

function getLegalAccountId(config: PayrollContributionConfigFragment) {
  if (config.account.__typename === 'ManagedSubAccount') {
    return config.account.legalAccount.legacyId;
  }
  return null;
}

function hasAny401kConfigs(configs: PayrollContributionConfigFragment[]) {
  return configs.some(config => config.account.__typename === 'ManagedSubAccount');
}

function getEditUrl(config: RecurringTransactionConfigFragment): { editUrl: string | null; intent: string | null } {
  let url: string | null = null;
  let intent: string | null = null;
  const configId = String(config.legacyId || config.legacyUuid || '');
  if (configId !== '') {
    switch (config.legacyTransferType) {
      case LegacyTransferType.Deposit:
        url = urlBuilder.appAutomaticDepositConfigEdit.absolute({
          automaticDepositConfigId: configId,
        });
        intent = 'Deposit';
        break;
      case LegacyTransferType.Withdrawal:
        url = urlBuilder.appAutomaticWithdrawalConfigEdit.absolute({
          automaticWithdrawalConfigId: configId,
        });
        intent = 'Withdrawal';

        break;
      case LegacyTransferType.Transfer:
        url = urlBuilder.appRecurringTransferConfigEdit.absolute({
          recurringTransferConfigId: configId,
        });
        intent = 'Transfer';

        break;
      default:
        break;
    }
  }
  const currentPath = encodeURIComponent(window.location.pathname);
  const returnTo = `return_to=${currentPath}`;
  const editUrl = url ? `${url}?${returnTo}` : null;
  return { editUrl, intent };
}

export const RecurringTransactionsCardView = ({
  recurringTransactionConfigs,
  isWithdrawal,
  recurringTransactionGuidance = [],
  payrollContributionConfigs = [],
  disclosures,
  subAccountId,
}: RecurringTransactionsCardViewProps) => {
  const shouldShowRecurringTransactionSetup = !payrollContributionConfigs.length && !recurringTransactionConfigs.length;
  const shouldShowSavingsHeader = !!(disclosures && disclosures.shortDisclosure && disclosures.longDisclosure);

  const legalAccountIdValue =
    payrollContributionConfigs.length > 0 ? getLegalAccountId(payrollContributionConfigs[0]) : null;

  const guidanceMessage = recurringTransactionGuidance?.[0];
  const { isVisible: isGuidanceInitiallyVisible } = useMessageVisibility({ messageId: guidanceMessage?.id || '' });
  const [isGuidanceDismissed, setIsGuidanceDismissed] = useState(false);
  const shouldShowCardFooter =
    recurringTransactionGuidance?.length > 0 && isGuidanceInitiallyVisible && !isGuidanceDismissed;

  const deferralSettingEditUrl = legalAccountIdValue
    ? urlBuilder.appDeferralSetting.absolute({
        legalAccountId: String(legalAccountIdValue),
        return_to: window.location.pathname,
      })
    : undefined;

  return (
    <Card>
      <Card.Header>
        <H2 variant="title200" css={{ padding: 'space.300' }}>
          {shouldShowRecurringTransactionSetup ? 'Automate your savings' : 'Recurring transactions'}
        </H2>
      </Card.Header>
      <Separator css={{ fullWidthWithInset: 'space.300' }} />
      <Card.Content>
        {shouldShowRecurringTransactionSetup ? (
          <>
            <RecurringTransactionsSetup
              subText={disclosures?.description}
              setupUrl={handleSetupRecurringDeposit(subAccountId)}
            />
            {shouldShowSavingsHeader && (
              <RecurringTransactionsDisclosure
                shortDisclosure={disclosures.shortDisclosure}
                longDisclosure={disclosures.longDisclosure}
              />
            )}
          </>
        ) : (
          <>
            {payrollContributionConfigs.length > 0 && (
              <>
                {hasAny401kConfigs(payrollContributionConfigs) && (
                  <ListRow css={{ paddingTop: 'space.300', paddingBottom: 'space.0' }}>
                    <ListRow.LeadingContent>
                      <H1 variant="title100">401(k) contribution settings</H1>
                    </ListRow.LeadingContent>
                    {deferralSettingEditUrl && (
                      <ListRow.TrailingContent>
                        <SambaAutoLink
                          to={deferralSettingEditUrl}
                          onClick={() => trackClick({ name: 'DeferralSettingEditIntended' })}
                        >
                          Edit
                        </SambaAutoLink>
                      </ListRow.TrailingContent>
                    )}
                  </ListRow>
                )}
                <Box
                  css={{
                    display: 'grid',
                    gap: 'space.300',
                    padding: 'space.300',
                  }}
                >
                  <PayrollConfigsList configs={payrollContributionConfigs} />
                </Box>
              </>
            )}

            {recurringTransactionConfigs.length > 0 && (
              <>
                {payrollContributionConfigs.length > 0 && (
                  <>
                    <Separator css={{ fullWidthWithInset: 'space.300' }} />
                    <ListRow css={{ paddingTop: 'space.300', paddingBottom: 'space.0' }}>
                      <ListRow.LeadingContent>
                        <H1 variant="title100">Other transactions</H1>
                      </ListRow.LeadingContent>
                    </ListRow>
                  </>
                )}
                {recurringTransactionConfigs.map(config => {
                  const { editUrl, intent } = getEditUrl(config);
                  return (
                    <ListRow
                      key={config.id}
                      css={{ paddingTop: 'space.300', paddingBottom: 'space.0', _last: { paddingBottom: 'space.300' } }}
                    >
                      <RecurringTransactionsConfig key={config.id} config={config} isWithdrawal={isWithdrawal} />
                      {editUrl && intent && config.isEditable && (
                        <ListRow.TrailingAsset>
                          <SambaAutoLink to={editUrl} onClick={() => trackClick({ name: `Auto${intent}Intended` })}>
                            Edit
                          </SambaAutoLink>
                        </ListRow.TrailingAsset>
                      )}
                    </ListRow>
                  );
                })}
              </>
            )}
          </>
        )}
      </Card.Content>
      {shouldShowCardFooter && (
        <Card.Footer
          css={{
            marginX: 'space.300',
            marginBottom: 'space.300',
          }}
        >
          <MessageBox
            messages={recurringTransactionGuidance}
            location="envelope_overview_recurring_transactions"
            onDismiss={() => setIsGuidanceDismissed(true)}
          />
        </Card.Footer>
      )}
    </Card>
  );
};
